import {z} from 'zod';

import i18n from '@dms/i18n';

import {
  accountingMatrixDatagridCodeObjectField,
  AccountingMatrixDatagridConfig,
  getAccountingMatrixDatagridSharedFields,
} from '../../utils/accountingMatrixDatagridConfig';

const isWorkshopApplication = (values: Record<string, unknown>) =>
  values.application === 'workshop';

/**
 * `warehouseNo`/`cashRegisterAccount`/`advancePaymentAccount` aren't in the swagger's
 * `required` list, but M205/Operation Items both turned out to have NOT NULL DB columns BE
 * doesn't validate at the API layer (T20-137885/T20-137887) — made required here as a
 * precaution too. Relax back to optional if BE confirms they're actually nullable.
 * `authorizationProfileCode`/`orderVariantSuffix` stay conditional (workshop-only per the epic,
 * matching M202's own swagger `required` list, which — unlike M205's — doesn't include them);
 * not live-tested with a non-workshop application yet, so this may need the same fix M205 got
 * if BE turns out to require them unconditionally here too.
 */

export const getAccountingMatrixDatagridM202HeadersConfig = (): AccountingMatrixDatagridConfig => ({
  dgCode: 'm202-headers',
  fields: [
    {
      name: 'headerCode',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldCode'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'headerOperationName',
      label: i18n.t('general.labels.name'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'application',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldApplication'),
      type: 'choice',
      codelistCategory: 'ACCapplicationModule',
      isRequired: true,
      ...accountingMatrixDatagridCodeObjectField('application'),
    },
    // Only used when application === 'workshop', see epic T20-134805 / T20-137883.
    {
      name: 'authorizationProfileCode',
      label: i18n.t(
        'page.accounting.labels.accountingMatrixDatagridsFieldAuthorizationProfileCode'
      ),
      type: 'text',
      showWhen: isWorkshopApplication,
    },
    {
      name: 'orderVariantSuffix',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldOrderVariantSuffix'),
      type: 'text',
      showWhen: isWorkshopApplication,
    },
    {
      name: 'warehouseNo',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldVehicleWarehouseNumber'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'operationArea',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldOperationArea'),
      type: 'choice',
      codelistCategory: 'ACCoperationArea',
      isRequired: true,
      ...accountingMatrixDatagridCodeObjectField('operationArea'),
    },
    {
      name: 'vatRateCode',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldVatRateCode'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'vatCode',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldVatCode'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'invoiceSeries',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldInvoiceSeries'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'vatSumOverLimit',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldVatSumOverLimit'),
      type: 'switch',
    },
    {
      name: 'vatPayer',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldVatPayer'),
      type: 'switch',
    },
    {
      name: 'cashRegisterAccount',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldCashRegisterAccount'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'advancePaymentAccount',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldAdvancePaymentAccount'),
      type: 'text',
      isRequired: true,
    },
    {
      name: 'isUpsale',
      label: i18n.t('page.accounting.labels.accountingMatrixDatagridsFieldUpsale'),
      type: 'switch',
    },
    ...getAccountingMatrixDatagridSharedFields(),
  ],
  schema: z.object({
    headerCode: z.string().min(1),
    headerOperationName: z.string().min(1),
    application: z.string().min(1),
    authorizationProfileCode: z.string().nullable().optional(),
    orderVariantSuffix: z.string().nullable().optional(),
    warehouseNo: z.string().min(1),
    operationArea: z.string().min(1),
    vatRateCode: z.string().min(1),
    vatCode: z.string().min(1),
    invoiceSeries: z.string().min(1),
    vatSumOverLimit: z.boolean().default(false),
    vatPayer: z.boolean().default(false),
    cashRegisterAccount: z.string().min(1),
    advancePaymentAccount: z.string().min(1),
    isUpsale: z.boolean().default(false),
    billingInformation: z.string().min(1),
    validFrom: z.string(),
    validTo: z.string(),
  }),
});
